/**
 * useAssessRegulatoryChange (P158) -- POST a regulatory change's text and
 * get back the affected obligations + compliance exposure (Engine,
 * grounded). Mutation: the Engine call is slow (EngineBusy on the button).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  ChangeAssessment,
  DraftRemediationCapIn,
  DraftRemediationCapOut,
  RegulatoryChangeAssessment,
} from "shared/api/types";

export function useAssessRegulatoryChange(): UseMutationResult<
  RegulatoryChangeAssessment,
  ApiError,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changeText: string) =>
      api.post<RegulatoryChangeAssessment>("/v1/framework/regulatory-change/assess", {
        change_text: changeText,
      }),
    // Round 19 A42 -- the assessment is on the record when it is made.
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["regulatory-change", "assessments"] }),
  });
}

/**
 * C9 -- the manual path. Only the JUDGING half of an assessment needs
 * the Engine; retrieval is pg_trgm over the obligation text and works
 * either way. With the Engine off this is what an operator gets
 * instead of a text box, a button and a 503.
 */
export interface CandidateObligation {
  obligation_id: string;
  natural_id: string;
  title: string;
  source_framework: string | null;
  control_count: number;
  gap_count: number;
}

export interface CandidateObligations {
  candidates: CandidateObligation[];
  candidates_considered: number;
}

export function useFindCandidateObligations(): UseMutationResult<
  CandidateObligations,
  ApiError,
  string
> {
  return useMutation({
    mutationFn: (change_text: string) =>
      api.post<CandidateObligations>("/v1/framework/regulatory-change/candidates", {
        change_text,
      }),
  });
}

/**
 * useDraftRemediationCAP (P159) -- the "act" step. POST the affected
 * obligations + the circular text; the backend creates a draft CAP
 * (anchored to a RegulatorLetter for the circular) pre-linked to the
 * gap controls, and returns the new CAP id to navigate to.
 */
export function useDraftRemediationCAP(): UseMutationResult<
  DraftRemediationCapOut,
  ApiError,
  DraftRemediationCapIn
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body: DraftRemediationCapIn) =>
      api.post<DraftRemediationCapOut>(
        "/v1/framework/regulatory-change/draft-cap",
        body,
      ),
  });
}

/**
 * useChangeAssessments (P165) -- the persisted regulatory-change lineage:
 * every circular the autopilot acted on + the CAP it raised, newest-first.
 */
export function useChangeAssessments(): UseQueryResult<ChangeAssessment[], ApiError> {
  return useQuery({
    queryKey: ["regulatory-change", "assessments"],
    queryFn: () =>
      api.get<ChangeAssessment[]>("/v1/framework/regulatory-change/assessments"),
  });
}

/**
 * Round 19 N13 -- hand an assessed circular to the preparers to draft.
 * Drafting the plan is the preparer's; an approver who assessed it had
 * no way to send it on. Returns how many were told now.
 */
export function useHandToPreparers(): UseMutationResult<
  { told: number },
  ApiError,
  string
> {
  return useMutation({
    meta: { handled: true },
    mutationFn: (assessmentId: string) =>
      api.post<{ told: number }>(
        `/v1/framework/regulatory-change/assessments/${assessmentId}/hand-to-preparers`,
        {},
      ),
  });
}
